import React, { useState, useRef, useEffect } from "react";
import { Provider, useSelector, shallowEqual } from "react-redux";
import store from "../store";
import settings from "../settings";
import theme from "../themes";
import styled, { ThemeProvider, StyleSheetManager } from "styled-components";
import root from "react-shadow/styled-components";
import useSize from "../hooks/useSize";
import { useTranslation } from "react-i18next";
import { recsApi } from "../services/recommendations";
import { widgetSettingsApi } from "../services/widgetsSettings";
import Slider from "../components/Slider";
import { getUrlParams, capitalizeTitle, detectChannel } from "../utils";
import { useTrackerMutation } from "../services/tracker";
import { useMemo } from "react";

import get from "lodash/get";
import find from "lodash/find";
import map from "lodash/map";
import take from "lodash/take";
import flow from "lodash/flow";
import isEmpty from "lodash/isEmpty";

const { excludeRecentFrom, widgetConfig } = settings;

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.colors.mainBgColor};
  background: inherit;
  font-family: ${({ theme }) => theme.fontFamily};
`;

const SliderWrapper = ({
  id = "",
  width,
  title = "",
  rows = 1,
  maxItemsPerRow = 6,
  onGameClick,
  fetchParams = {},
  fetchUrl,
  prerequisiteRequests,
}) => {
  const [trackEvent] = useTrackerMutation();

  const channel = useMemo(() => detectChannel(), []);
  const user = useSelector((state) => state.user, shallowEqual);
  const { t } = useTranslation();

  const location = fetchParams.location ?? id;

  const trackClick = (gameId, position) => {
    const url = window.location.href;
    const options = {
      data: {
        game_id: gameId,
        page: url,
        position,
        location,
      },
      event_type: "clicks:game",
      user_id: user?.userId ?? "0",
    };

    trackEvent(options);
  };

  const trackImpressions = (visibleGameIds) => {
    const url = window.location.href;

    const options = {
      channel,
      data: {
        game_ids: visibleGameIds.map((gameId) => ({
          id: gameId,
        })),
        page: url,
        location,
      },
      event_type: "impressions:games",
      user_id: user?.userId ?? "0",
    };

    trackEvent(options);
  };

  const key = fetchUrl?.split("/").at(-1);

  const isSimilarGamesRequest = key === "similar";

  const fetcher = get(recsApi, `useGetRecommendationsQuery`);

  const isCountryRequest = prerequisiteRequests?.includes("userCountry")
    ? true
    : false;

  const excludeLastGamesPlayed =
    excludeRecentFrom && excludeRecentFrom.find((i) => i === key);

  const { data, error, isFetching } = fetcher({
    user,
    args: {
      key: id,
      ...fetchParams,
      location,
      //For the Top 10 In Country carousel we request a few more games to filter unwanted results and always display 10 games.
      count: isCountryRequest ? fetchParams.count + 5 : fetchParams.count,
      //Exclude last 5 games a user played from specific swimlane if specified in operator settings.
      ...(excludeLastGamesPlayed && {
        exclude_last_n_played: 5,
      }),
    },
    fetchUrl,
    prerequisiteRequests,
  });

  let titleSuffix = data?.titleSuffix;

  if (isCountryRequest) {
    const countryISOCode = titleSuffix?.toUpperCase() || "GR";
    titleSuffix = t(countryISOCode);
  } else if (isSimilarGamesRequest) {
    titleSuffix = data?.similar_game_name;
  }

  const sliderItems = isCountryRequest
    ? take(data?.results, fetchParams.count)
    : data?.results;

  const sliderTitle = flow(
    //The id of the slider is the key of the translated title.If it does not exist then use the default title provided without translating.
    (title) => capitalizeTitle(title),
    (formattedTitle) => t(id, { defaultValue: formattedTitle }),
    (translatedTitle) =>
      titleSuffix ? `${translatedTitle} ${titleSuffix}` : translatedTitle,
  )(title);

  const noItems = !isFetching && (error || isEmpty(sliderItems));

  if (noItems) return null;

  return (
    <Slider
      items={isFetching ? null : sliderItems}
      width={width}
      isLoading={isFetching}
      title={sliderTitle}
      rows={rows}
      onItemClick={({ gameInfo = {} }) => {
        const { gameId, position } = gameInfo;
        trackClick(gameId, position);
        onGameClick?.({ gameInfo });
      }}
      trackClick={trackClick}
      onViewportChange={trackImpressions}
      maxItemsPerRow={maxItemsPerRow}
      showCount={isCountryRequest}
    />
  );
};

function reorderCarousels(carouselsData, order) {
  const orderedCarousels = [];
  const widgetMap = new Map(carouselsData.map((w) => [w.id, w]));

  // Add widgets in the specified order
  order.forEach((id) => {
    if (widgetMap.has(id)) {
      orderedCarousels.push(widgetMap.get(id));
      widgetMap.delete(id);
    }
  });

  // Add any remaining widgets not in the order list
  widgetMap.forEach((widget) => orderedCarousels.push(widget));
  return orderedCarousels;
}

const CarouselWidget = ({ onGameClick, widgetId }) => {
  const [carousels, setCarousels] = useState([]);
  // const dispatch = useDispatch();

  const target = useRef(null);
  const size = useSize(target);

  const widgetWidth = get(size, "width");
  const widgetTheme = {
    ...theme,
    widgetWidth,
  };

  let data;
  let isFetching = false;
  let error = null;

  if (widgetConfig) {
    data = widgetConfig.pages;
  } else {
    const fetcher = get(widgetSettingsApi, `useGetWidgetSettingsQuery`);
    const result = fetcher(); // Call the hook or function
    data = result.data;
    isFetching = result.isFetching;
    error = result.error;
  }

  const { filters: widgetFilters, location } = useSelector(
    (state) => state.widgets[widgetId] ?? {},
    shallowEqual,
  );

  // const location = window.location;
  // const activePageUrl = (location?.hash || location?.href || "")
  //   .replace("#", "")
  //   .split("/")
  //   .pop();

  const user = useSelector((state) => state.user, shallowEqual);
  const { userId, order: userOrder } = user || {};

  useEffect(() => {
    const { pageId } = getUrlParams();
    const pageSettings =
      find(data, (page) => page?.pageId === pageId) ??
      //TODO: remove this fallback (loads widgets of first page if no page found with given url)
      data?.[0];

    let widgetSettings = [];
    if (!isFetching) {
      widgetSettings = map(pageSettings?.elements, (elem) => ({
        ...elem?.properties,
        id: elem.id,
      }));

      if (userId && userOrder) {
        widgetSettings = reorderCarousels(widgetSettings, userOrder);
      }
      setCarousels(widgetSettings);
    }
  }, [isFetching, data, userId, userOrder]);

  useEffect(() => {
    const handleMessage = (e) => {
      let { jsonData, productType } = e.data;
      if (productType === "casino" && jsonData) {
        if (userId && userOrder) {
          const orderedCarousels = reorderCarousels(jsonData, userOrder);
          jsonData = orderedCarousels;
        }
        setCarousels(jsonData);
      }

      // authToken && dispatch(setAuthToken({ token: authToken }));
    };

    window.addEventListener("message", handleMessage, false);
    return () => window.removeEventListener("message", handleMessage, false);
  }, [userId, userOrder]);

  if (error) return null;

  return (
    <ThemeProvider theme={widgetTheme}>
      <Wrapper ref={target} data-test="carousel-widget-wrapper">
        {carousels.map((props, key) => {
          let fetchParams = props?.fetchParams ?? {};
          if (widgetFilters) {
            const defaultFilters = fetchParams?.filters;
            const filters = defaultFilters
              ? defaultFilters.concat(";", widgetFilters)
              : widgetFilters;

            fetchParams = { ...fetchParams, filters };
          }

          return (
            <SliderWrapper
              {...props}
              data-test="slider-wrapper"
              onGameClick={onGameClick}
              key={key}
              width={widgetWidth}
              fetchParams={{ ...fetchParams, location }}
            />
          );
        })}
      </Wrapper>
    </ThemeProvider>
  );
};

export default function CarouselWidgetProvider(props) {
  return (
    <Provider store={store}>
      <StyleSheetManager disableCSSOMInjection>
        <root.div className="shadow-host">
          <CarouselWidget {...props} />
        </root.div>
      </StyleSheetManager>
    </Provider>
  );
}
